/**
 * TTEHeadline — the macro Time-to-Exploit strip for the cyber overview + war
 * room. Frames the whole cyber posture in velocity terms:
 *
 *   ⏱ Median time-to-exploit: 5.2 days and shrinking · 398 CVEs we'd lose the
 *      patch race on · N AI-accelerated
 *
 * Pulls from /api/cyber/tte-summary. Links to the Zero Day Clock (trend) and the
 * Exploitation Race (the losing list). Fail-soft: renders nothing if the summary
 * is unavailable or empty so it never blanks the page.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Timer, TrendingDown, Flame, Zap } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface TTESummary {
  median_tte_days_current_year: number
  tte_trend: string
  decay_rate_per_year: number
  losing_race_count: number
  median_exposure_days: number
  ai_accelerated_count: number
}

export default function TTEHeadline({ compact }: { compact?: boolean }) {
  const [s, setS] = useState<TTESummary | null>(null)

  useEffect(() => {
    let mounted = true
    api.get('/api/cyber/tte-summary')
      .then(r => { if (mounted) setS(r.data) })
      .catch(() => { /* fail-soft */ })
    return () => { mounted = false }
  }, [])

  if (!s) return null
  const median = s.median_tte_days_current_year
  // Nothing meaningful yet → don't render a hollow strip.
  if (!median && !s.losing_race_count) return null

  const medianLabel = median <= 0 ? 'now' : `${median % 1 === 0 ? median : median.toFixed(1)} day${median === 1 ? '' : 's'}`

  return (
    <div className={clsx('card border border-rose/30 bg-gradient-to-r from-rose/10 to-transparent',
      compact ? 'px-3 py-1.5' : 'px-4 py-2.5')}>
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[12px]">
        <span className="inline-flex items-center gap-1.5 font-semibold text-text-primary">
          <Timer size={compact ? 13 : 15} className="text-rose-light" />
          Median time-to-exploit:
          <span className="text-rose-light font-bold">{medianLabel}</span>
          {s.tte_trend === 'shrinking' && (
            <span className="inline-flex items-center gap-0.5 text-amber-light">
              <TrendingDown size={12} /> and shrinking
            </span>
          )}
        </span>
        <Link to="/app/exploitation-race"
          className="inline-flex items-center gap-1 text-text-secondary hover:text-rose-light"
          title="View the CVEs whose exploit would land before we could patch">
          <Flame size={12} className="text-rose-light" />
          <b className="text-rose-light">{s.losing_race_count}</b> CVEs we'd lose the patch race on
        </Link>
        {s.ai_accelerated_count > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-light"
            title="CVEs whose exploitation window was collapsed by the AI-discovered accelerant">
            <Zap size={12} /> <b>{s.ai_accelerated_count}</b> AI-accelerated
          </span>
        )}
        <Link to="/app/zero-day-clock"
          className="ml-auto text-[10px] text-blue-light hover:underline shrink-0"
          title="Macro TTE trend + decay model">
          Zero Day Clock →
        </Link>
      </div>
    </div>
  )
}
